import { useQuery } from '@tanstack/react-query';
import api from '../../api/client';
import { parsePhotoPresetToken } from '../photoPresetId';
import { queryKeys } from './queryKeys';
import useProtectedQueryEnabled from './useProtectedQueryEnabled';

function appendReferenceUrls(target, value) {
  if (!value) return;
  if (Array.isArray(value)) {
    value.forEach((url) => appendReferenceUrls(target, url));
    return;
  }
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      parsed.forEach((url) => appendReferenceUrls(target, url));
      return;
    }
  } catch {
    /* plain URL */
  }
  target.push(value);
}

export function normaliseGalleryItem(raw) {
  if (!raw) return null;

  const type = raw.type || (raw.videoUrl ? 'video' : 'photo');
  const referenceImageUrls = [];
  appendReferenceUrls(referenceImageUrls, raw.referenceImageUrls);
  appendReferenceUrls(referenceImageUrls, raw.referenceUrl);
  appendReferenceUrls(referenceImageUrls, raw.srcImageUrl);
  appendReferenceUrls(referenceImageUrls, raw.referenceImageUrl);
  const normalizedReferenceImageUrls = Array.from(new Set(referenceImageUrls.filter(Boolean)));

  const rawPreset = raw.presetId || raw.preset;
  const parsedPreset = rawPreset ? parsePhotoPresetToken(rawPreset) : null;

  return {
    id: raw.id,
    type,
    imageUrl: raw.imageUrl || null,
    videoUrl: raw.videoUrl || null,
    thumbnailUrl: raw.thumbnailUrl || null,
    presetId: parsedPreset?.presetId || null,
    prompt: type === 'video' ? null : raw.prompt || null,
    userPrompt: type === 'video' ? null : raw.userPrompt || raw.prompt || null,
    aspectRatio: raw.aspectRatio || null,
    quality: raw.quality || parsedPreset?.quality || null,
    durationSec: raw.durationSec ?? null,
    width: raw.width ?? null,
    height: raw.height ?? null,
    model: raw.model || null,
    srcImageUrl: raw.srcImageUrl || null,
    referenceImageUrl: normalizedReferenceImageUrls[0] || null,
    referenceImageUrls: normalizedReferenceImageUrls,
    createdAt: raw.createdAt || null,
    status: raw.status || 'completed',
    errorMessage: raw.errorMessage || null,
  };
}

export async function fetchGalleryItem(id, signal) {
  const res = await api.get('/gallery', {
    params: { limit: 100 },
    signal,
  });
  const list = Array.isArray(res?.data?.items) ? res.data.items : [];
  const found = list.find((item) => String(item?.id) === String(id));
  return {
    item: normaliseGalleryItem(found),
    photoItems: list.map(normaliseGalleryItem).filter((item) => item?.type === 'photo'),
  };
}

export function useGalleryItemQuery(id, options = {}) {
  const { enabled = true, ...rest } = options;
  const protectedEnabled = useProtectedQueryEnabled(Boolean(id) && enabled);
  return useQuery({
    queryKey: queryKeys.galleryItem(id),
    queryFn: ({ signal }) => fetchGalleryItem(id, signal),
    ...rest,
    enabled: protectedEnabled,
  });
}
